// Abstracted Level System for Musical Puzzle Games

export type TimeSignature = [number, number]; // [numerator, denominator] e.g., [4, 4] or [3, 4]

export interface Note {
  note: string;          // Musical note or 'rest'
  time: string;          // Time within fragment (0-fragment duration)
  duration: string;      // Duration as fraction of fragment
}

export interface Fragment {
  id: string;
  name: string;
  voice: 'right' | 'left' | 'melody' | 'harmony' | 'bass';
  notes: Note[];
  beatDuration: number;  // Duration in beats (e.g., 4.25 for 4¼ beats)
  correctPosition: {
    beat: number;        // Target beat position (0-based)
    pitch: number;       // Semitone offset for playback
  };
}

export type HintState = 'inactive' | 'partial' | 'correct';

export interface FragmentPairHint {
  type: 'fragment-pair';
  fragmentId1: string;
  fragmentId2: string;
  fragment1Name: string;
  fragment2Name: string;
  expectedDistance: number;
  correctOrder: [string, string]; // [first, second] in correct order
  check: (positions: Map<string, number>) => HintState;
}

export type SolutionHint = FragmentPairHint;

export interface LevelConfig {
  id: string;
  name: string;
  description: string;
  
  // Timeline Configuration
  totalBeats: number;           // Total beats in the level
  tempo: number;                // BPM
  key: string;                  // Musical key
  timeSignature: TimeSignature; // Time signature for the level
  
  // Fragment Configuration
  fragments: Fragment[];        // The fragments to arrange
  
  // Solution Hints
  solutionHints: SolutionHint[];
  
  // Difficulty/Hints
  showBeatNumbers: boolean;
  allowPartialSolutions: boolean;
}

// Generate all fragment pair hints for a level
export const generateFragmentPairHints = (fragments: Fragment[]): FragmentPairHint[] => {
  const hints: FragmentPairHint[] = [];
  
  // Generate all unique pairs
  for (let i = 0; i < fragments.length; i++) {
    for (let j = i + 1; j < fragments.length; j++) {
      const frag1 = fragments[i];
      const frag2 = fragments[j];
      
      // Determine correct order based on correct positions
      const [first, second] = frag1.correctPosition.beat <= frag2.correctPosition.beat 
        ? [frag1, frag2] 
        : [frag2, frag1];
      
      // Calculate expected distance
      const expectedDistance = Math.abs(frag1.correctPosition.beat - frag2.correctPosition.beat);
      
      hints.push({
        type: 'fragment-pair',
        fragmentId1: frag1.id,
        fragmentId2: frag2.id,
        fragment1Name: frag1.name,
        fragment2Name: frag2.name,
        expectedDistance,
        correctOrder: [first.id, second.id],
        check: (positions) => {
          const pos1 = positions.get(frag1.id);
          const pos2 = positions.get(frag2.id);
          
          // If either fragment is not placed, hint is inactive
          if (pos1 === undefined || pos2 === undefined) {
            return 'inactive';
          }
          
          // Check distance first
          const actualDistance = Math.abs(pos1 - pos2);
          if (actualDistance === expectedDistance) {
            // If they're at the correct distance, check order
            // For same-beat (distance 0), order doesn't matter
            if (expectedDistance === 0) {
              return 'correct';
            }
            
            // For different beats, check if order is correct
            const actualFirst = pos1 < pos2 ? frag1.id : frag2.id;
            const actualSecond = pos1 < pos2 ? frag2.id : frag1.id;
            const correctOrderMaintained = 
              actualFirst === first.id && actualSecond === second.id;
            
            return correctOrderMaintained ? 'correct' : 'inactive';
          }
          
          // Wrong distance - check if at least in correct direction
          // If they should be at same beat (distance 0), any other distance is wrong
          if (expectedDistance === 0) {
            return 'inactive';
          }
          
          // Check if they're in the correct relative direction
          // first.id is the fragment that should come first (lower beat position)
          // If first.id matches frag1.id, then frag1 should be before frag2 (pos1 < pos2)
          // If first.id matches frag2.id, then frag2 should be before frag1 (pos2 < pos1)
          const correctDirection = (first.id === frag1.id) ? (pos1 < pos2) : (pos2 < pos1);
          
          // White hint only if they're in the correct direction but wrong distance
          return correctDirection ? 'partial' : 'inactive';
        }
      });
    }
  }
  
  return hints;
};

// Utility to create beat drop zone IDs
export const createBeatId = (beatIndex: number): string => {
  return `beat-${beatIndex}`;
};

// Utility to parse beat ID back to index
export const parseBeatId = (beatId: string): number | null => {
  const match = beatId.match(/^beat-(\d+)$/);
  if (!match) return null;
  
  return parseInt(match[1], 10);
};